import { Share, StyleSheet, Text, View } from 'react-native'
import { BottomModalButton } from '../../../BottomModalContent/BottomModalButton'
import { color, fontFamily } from '../../../../../theme'
import { formatChipsNumber } from '@/components/GameAssets/Player/PlayerBox'
import { buildDaily10ShareMessage } from '../../../../../utils/daily10Share'
import { GAME_CONSTANTS } from '@ppwr/convex/shared/constants'
import { router } from 'expo-router'

const { DEFAULT_MAX_POSSIBLE_CHIPS, DEFAULT_GAME_NAME } = GAME_CONSTANTS.DAILY10_CONSTANTS

type HandOutcome = 'free_fold' | 'win' | 'loss'

interface Daily10ResultsProps {
  finalChips: number
  countedHands: number
  preflopFoldsUsed: number
  preflopFoldsAllowed: number
  handOutcomes: HandOutcome[]
  playedDate: string // "YYYY-MM-DD"
  streak: number
  onClose: () => void
}

export const Daily10Results = ({
  finalChips,
  countedHands,
  preflopFoldsUsed,
  preflopFoldsAllowed,
  handOutcomes,
  playedDate,
  streak,
  onClose
}: Daily10ResultsProps) => {
  const maxChips = DEFAULT_MAX_POSSIBLE_CHIPS
  const percentage = Math.round((finalChips / maxChips) * 100)

  const handleLeaderboard = () => {
    router.replace('/(root)/daily10/leaderboard')
  }

  const handleShare = async () => {
    const message = buildDaily10ShareMessage({ handOutcomes, playedDate, finalChips, streak })
    await Share.share({ message })
  }

  return (
    <View style={styles.container}>
      <Text style={styles.label}>{DEFAULT_GAME_NAME} Complete</Text>
      <Text style={styles.score}>{formatChipsNumber(finalChips)}</Text>
      <Text style={styles.subtitle}>
        out of {formatChipsNumber(maxChips)} chips ({percentage}%)
      </Text>

      <View style={styles.progressBar}>
        <View style={[styles.progressFill, { width: `${Math.min(percentage, 100)}%` as any }]} />
      </View>

      <View style={styles.stats}>
        <View style={styles.statRow}>
          <Text style={styles.statLabel}>Hands Played</Text>
          <Text style={styles.statValue}>{countedHands}</Text>
        </View>
        <View style={styles.statRow}>
          <Text style={styles.statLabel}>Pre-flop Folds Used</Text>
          <Text style={styles.statValue}>
            {preflopFoldsUsed}/{preflopFoldsAllowed}
          </Text>
        </View>
      </View>

      <View style={styles.buttons}>
        <BottomModalButton
          text="Share"
          onPress={handleShare}
          size="large"
          alternateFont
          textStyle={[styles.buttonText]}
        />
        <BottomModalButton
          text="Leaderboard"
          onPress={handleLeaderboard}
          size="large"
          alternateFont
          alternateColor
          textStyle={[styles.buttonText]}
        />
        <BottomModalButton
          text="Done"
          onPress={onClose}
          size="large"
          alternateFont
          alternateColor
          textStyle={[styles.buttonText]}
        />
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: 24,
    paddingHorizontal: 24,
    gap: 16,
    alignItems: 'center'
  },
  label: {
    fontFamily: fontFamily.GTAmericaStandardRegular,
    fontSize: 14,
    color: color.palette.grey,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 1
  },
  score: {
    fontFamily: fontFamily.FeatureDeckBold,
    fontSize: 48,
    textAlign: 'center',
    color: color.palette.black
  },
  subtitle: {
    fontFamily: fontFamily.GTAmericaStandardRegular,
    fontSize: 14,
    textAlign: 'center',
    color: color.palette.grey
  },
  progressBar: {
    width: '100%',
    height: 8,
    backgroundColor: color.palette.lightGrey,
    borderRadius: 4,
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    backgroundColor: color.palette.maroon,
    borderRadius: 4
  },
  stats: {
    width: '100%',
    gap: 8
  },
  statRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: color.palette.lightGrey
  },
  statLabel: {
    fontFamily: fontFamily.GTAmericaStandardRegular,
    fontSize: 14,
    color: color.palette.grey
  },
  statValue: {
    fontFamily: fontFamily.GTAmericaStandardMedium,
    fontSize: 14,
    color: color.palette.black
  },
  buttons: {
    width: '100%',
    gap: 8
  },
  buttonText: {
    fontSize: 16,
    paddingVertical: 5
  }
})
